import type {
  CheckInRepository,
  DiaryRepository,
  FoodRepository,
  GoalRepository,
  NutrientTargetRepository,
  OnboardingWeightReferenceRepository,
  RecipeRepository,
  WeekPlanRepository,
  WeightRepository,
} from "../../../application/ports/repositories";
import type { TransactionManager } from "../../../application/ports/transaction-manager";
import type { PlanningConfiguration, PlanningConfigurationRepository } from "../../../application/ports/planning-configuration";
import type { CheckIn } from "../../../domain/check-in/check-in";
import type { DiaryEntry } from "../../../domain/diary/diary-entry";
import type { Food } from "../../../domain/foods/food";
import type { GoalPhase } from "../../../domain/goals/goal";
import type { Recipe } from "../../../domain/recipes/recipe";
import type { DailyNutrientTarget } from "../../../domain/nutrition/nutrient-target";
import type { WeekPlan } from "../../../domain/planning/week-plan";
import type { CalendarDate } from "../../../domain/shared/calendar-date";
import type { WeightEntry } from "../../../domain/weight/weight-entry";
import type { OnboardingWeightReference } from "../../../domain/weight/onboarding-reference";
import type { DietTrackerDatabase } from "./database";
import {
  checkInFromRecord,
  checkInToRecord,
  diaryFromRecord,
  diaryToRecord,
  foodFromRecord,
  foodToRecord,
  goalFromRecord,
  goalToRecord,
  nutrientTargetFromRecord,
  nutrientTargetToRecord,
  onboardingReferenceFromRecord,
  onboardingReferenceToRecord,
  recipeFromRecord,
  recipeToRecord,
  weekPlanFromRecord,
  weekPlanToRecord,
  weightFromRecord,
  weightToRecord,
} from "./domain-mappers";
import { mutateDatabase } from "./mutate";
import { DexiePlanningConfigurationRepository } from "./planning-configuration-repository";

export class DexieFoodRepository implements FoodRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getById(id: string): Promise<Food | undefined> {
    const value = await this.database.foods.get(id);
    return value ? foodFromRecord(value) : undefined;
  }

  async findByBarcode(barcode: string): Promise<Food | undefined> {
    const value = await this.database.foods.where("barcode").equals(barcode).first();
    return value ? foodFromRecord(value) : undefined;
  }

  async findBySourceIdentity(provider: string, providerFoodId: string): Promise<Food | undefined> {
    const value = await this.database.foods
      .where("[source.provider+source.externalId]")
      .equals([provider, providerFoodId])
      .first();
    return value ? foodFromRecord(value) : undefined;
  }

  async search(query: string, limit = 20): Promise<readonly Food[]> {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return [];
    const values = await this.database.foods
      .filter((food) => `${food.brand ?? ""} ${food.name} ${food.barcode ?? ""}`.toLocaleLowerCase().includes(normalized))
      .limit(Math.max(1, limit))
      .toArray();
    return values.map(foodFromRecord);
  }

  async save(food: Food): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.foods.put(foodToRecord(food)); });
  }
}

export class DexieDiaryRepository implements DiaryRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getById(id: string): Promise<DiaryEntry | undefined> {
    const value = await this.database.diaryEntries.get(id);
    return value ? diaryFromRecord(value) : undefined;
  }

  async listBetween(start: CalendarDate, end: CalendarDate): Promise<readonly DiaryEntry[]> {
    const values = await this.database.diaryEntries.where("date").between(start, end, true, true).sortBy("createdAt");
    return values.map(diaryFromRecord);
  }

  async save(entry: DiaryEntry): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.diaryEntries.put(diaryToRecord(entry)); });
  }

  async delete(id: string): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.diaryEntries.delete(id); });
  }
}

export class DexieRecipeRepository implements RecipeRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getById(id: string): Promise<Recipe | undefined> {
    const value = await this.database.recipes.get(id);
    return value ? recipeFromRecord(value) : undefined;
  }

  async list(): Promise<readonly Recipe[]> {
    return (await this.database.recipes.orderBy("name").toArray()).map(recipeFromRecord);
  }

  async search(query: string, limit = 20): Promise<readonly Recipe[]> {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return [];
    const values = await this.database.recipes
      .filter((recipe) => recipe.name.toLocaleLowerCase().includes(normalized))
      .limit(Math.max(1, limit))
      .toArray();
    return values.map(recipeFromRecord);
  }

  async save(recipe: Recipe): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.recipes.put(recipeToRecord(recipe)); });
  }

  async delete(id: string): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.recipes.delete(id); });
  }
}

export class DexieWeightRepository implements WeightRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getByDate(date: CalendarDate): Promise<WeightEntry | undefined> {
    const value = await this.database.weightEntries.get(date);
    return value ? weightFromRecord(value) : undefined;
  }

  async listBetween(start: CalendarDate, end: CalendarDate): Promise<readonly WeightEntry[]> {
    const values = await this.database.weightEntries.where("date").between(start, end, true, true).sortBy("date");
    return values.map(weightFromRecord);
  }

  async saveForDate(entry: WeightEntry): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.weightEntries.put(weightToRecord(entry)); });
  }
}

export class DexieOnboardingWeightReferenceRepository implements OnboardingWeightReferenceRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async get(): Promise<OnboardingWeightReference | undefined> {
    const record = await this.database.onboardingReferences.get("onboarding");
    return record ? onboardingReferenceFromRecord(record) : undefined;
  }

  async saveOnce(reference: OnboardingWeightReference): Promise<void> {
    await mutateDatabase(this.database, async () => {
      if (await this.database.onboardingReferences.get("onboarding")) {
        throw new Error("Onboarding weight reference is immutable");
      }
      await this.database.onboardingReferences.add(onboardingReferenceToRecord(reference));
    });
  }
}

export class DexieGoalRepository implements GoalRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getActive(onDate: CalendarDate): Promise<GoalPhase | undefined> {
    const records = await this.database.goalPhases
      .where("effectiveFrom")
      .belowOrEqual(onDate)
      .toArray();
    const record = records.sort((left, right) =>
      left.effectiveFrom.localeCompare(right.effectiveFrom) ||
      left.createdAt.localeCompare(right.createdAt) ||
      left.id.localeCompare(right.id),
    ).at(-1);
    return record ? goalFromRecord(record) : undefined;
  }

  async getById(id: string): Promise<GoalPhase | undefined> {
    const record = await this.database.goalPhases.get(id);
    return record ? goalFromRecord(record) : undefined;
  }

  async save(goal: GoalPhase): Promise<void> {
    await mutateDatabase(this.database, async () => {
      if (await this.database.goalPhases.get(goal.id)) throw new Error("Goal revisions are immutable");
      await this.database.goalPhases.add(goalToRecord(goal));
    });
  }
}

export class DexieWeekPlanRepository implements WeekPlanRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getOpen(): Promise<WeekPlan | undefined> {
    const records = await this.database.weekPlans.where("status").equals("active").sortBy("startDate");
    const record = records.at(-1);
    return record ? weekPlanFromRecord(record) : undefined;
  }

  async getById(id: string): Promise<WeekPlan | undefined> {
    const record = await this.database.weekPlans.get(id);
    return record ? weekPlanFromRecord(record) : undefined;
  }

  async save(plan: WeekPlan): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.weekPlans.put(weekPlanToRecord(plan)); });
  }
}

export class DexieCheckInRepository implements CheckInRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async getLatestCompleted(): Promise<CheckIn | undefined> {
    const record = await this.database.checkIns.orderBy("completedAt").last();
    return record ? checkInFromRecord(record) : undefined;
  }

  async save(checkIn: CheckIn): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.checkIns.put(checkInToRecord(checkIn)); });
  }
}

export class DexieNutrientTargetRepository implements NutrientTargetRepository {
  constructor(private readonly database: DietTrackerDatabase) {}

  async list(): Promise<readonly DailyNutrientTarget[]> {
    return (await this.database.nutrientGoals.toArray()).map(nutrientTargetFromRecord);
  }

  async save(target: DailyNutrientTarget): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.nutrientGoals.put(nutrientTargetToRecord(target)); });
  }

  async delete(nutrientKey: string): Promise<void> {
    await mutateDatabase(this.database, async () => { await this.database.nutrientGoals.delete(nutrientKey); });
  }
}

export class DexieTransactionManager implements TransactionManager {
  constructor(private readonly database: DietTrackerDatabase) {}

  run<T>(work: () => Promise<T>): Promise<T> {
    return this.database.runUserDataTransaction(work);
  }
}

export interface DomainRepositoryBundle {
  foods: FoodRepository;
  recipes: RecipeRepository;
  diary: DiaryRepository;
  weights: WeightRepository;
  onboardingReference: OnboardingWeightReferenceRepository;
  goals: GoalRepository;
  weekPlans: WeekPlanRepository;
  checkIns: CheckInRepository;
  nutrientTargets: NutrientTargetRepository;
  configuration: PlanningConfigurationRepository;
  transaction: TransactionManager;
}

export function createDomainRepositories(
  database: DietTrackerDatabase,
  defaultPlanningConfiguration?: PlanningConfiguration,
): DomainRepositoryBundle {
  return {
    foods: new DexieFoodRepository(database),
    recipes: new DexieRecipeRepository(database),
    diary: new DexieDiaryRepository(database),
    weights: new DexieWeightRepository(database),
    onboardingReference: new DexieOnboardingWeightReferenceRepository(database),
    goals: new DexieGoalRepository(database),
    weekPlans: new DexieWeekPlanRepository(database),
    checkIns: new DexieCheckInRepository(database),
    nutrientTargets: new DexieNutrientTargetRepository(database),
    configuration: new DexiePlanningConfigurationRepository(database, defaultPlanningConfiguration),
    transaction: new DexieTransactionManager(database),
  };
}
